import { Component, type ErrorInfo, type ReactNode } from 'react';
import { ErrorScreen } from './ErrorScreen';

/**
 * What the app shows when something throws while the interface is drawing.
 *
 * Without it any such bug blanked the whole app with no word (QA 2026-09-24,
 * files-doc-F20). The editor below it still closes the way it always does — its unsent
 * edits go out, or are kept for next time — so a reload picks up where it was.
 *
 * A class because React has no other way to catch this, and Fast Refresh cannot keep a class:
 * re-running this module rebuilds everything under it, which is the whole app. So it imports
 * nothing that changes. Its screen lives in ErrorScreen.tsx; when this file imported the
 * buttons itself, saving them threw away the open editor (1 Nov).
 */
type State = { failed: boolean; error: unknown };

export class ErrorBoundary extends Component<{ children: ReactNode }, State> {
  override state: State = { failed: false, error: null };

  /**
   * Anything thrown is a crash, even a throw with nothing in it (`throw 0`, `throw ''`), so the
   * flag says so and not the error. The error is kept so a developer's machine can show what threw.
   */
  static getDerivedStateFromError(error: unknown): State {
    return { failed: true, error };
  }

  override componentDidCatch(error: unknown, info: ErrorInfo): void {
    console.error('The interface stopped', error, info.componentStack);
  }

  override render(): ReactNode {
    return this.state.failed ? <ErrorScreen error={this.state.error} /> : this.props.children;
  }
}
